'use client';

import React, { useState, useMemo } from 'react';
import {
  Search,
  SlidersHorizontal,
  RotateCcw,
  CheckCircle2,
  ShieldCheck,
  Truck,
  CreditCard,
  Phone,
  MessageCircle,
  Star,
  ChevronRight,
  ChevronDown,
  Car,
  Package,
  Check,
  ExternalLink,
  ArrowRight,
  Zap,
  Clock,
  Layers,
  Award,
  X,
  Store,
  Cpu,
  Sun,
  Radio,
  FileCheck
} from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import CategoryIcon from '@/components/CategoryIcon';
import { getCurrencyByCode } from '@/lib/countries';
import {
  GLOBAL_MAKES,
  YEARS as CATALOG_YEARS,
  ENGINE_CONFIGURATIONS,
  CAR_TRIMS,
  MAKE_TRIMS,
  PART_KEYWORDS,
  PART_BRANDS,
  matchProductToVehicle
} from '@/lib/catalog';
import VehicleBrandLogo from '@/components/VehicleBrandLogo';

interface ShopPageReplicaProps {
  products: any[];
  selectedCountry: string;
  isLoading?: boolean;
  onProductClick: (product: any) => void;
  onVendorClick: (vendor: any) => void;
  onVinSearch?: (vin: string) => void;
  onRequestPart?: () => void;
  onChatWithEve?: () => void;
  activeVehicle?: any;
  getWhatsAppHandoffUrl?: (product: any, vehicle: any) => string;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  selectedCategory?: string;
  onCategoryChange?: (categoryId: string) => void;
}

const ALL_MAKES = ['All Makes', ...GLOBAL_MAKES.map((m) => m.name)];
const ALL_YEARS = ['All Years', ...Array.from({ length: 32 }, (_, i) => String(2026 - i))];
const ALL_ENGINES = ['All Engines', ...ENGINE_CONFIGURATIONS];

export interface StandaloneCategory {
  id: string;
  name: string;
  canonical: string;
  icon: any;
  subcategories: string[];
}

const STANDALONE_CATEGORIES: StandaloneCategory[] = [
  {
    id: 'all',
    name: 'All Categories',
    canonical: 'ALL',
    icon: Layers,
    subcategories: []
  },
  {
    id: 'braking',
    name: 'Brakes',
    canonical: 'BRAKING',
    icon: ShieldCheck,
    subcategories: ['Brake Pads', 'Brake Rotors', 'Brake Calipers', 'Master Cylinders', 'Brake Boosters', 'Brake Hoses', 'ABS Sensors', 'Drum Brakes']
  },
  {
    id: 'engine',
    name: 'Engine Components',
    canonical: 'ENGINE',
    icon: Zap,
    subcategories: ['Timing Belt & Chain', 'Engine Mounts', 'Gaskets & Seals', 'Cylinder Heads', 'Pistons & Rings', 'Valves & Camshafts', 'Oil Pumps', 'Turbos & Superchargers']
  },
  {
    id: 'suspension',
    name: 'Suspension',
    canonical: 'SUSPENSION',
    icon: Award,
    subcategories: ['Shock Absorbers', 'Struts', 'Control Arms', 'Ball Joints', 'Sway Bar Links', 'Coil Springs', 'Bushings']
  },
  {
    id: 'steering',
    name: 'Steering',
    canonical: 'STEERING',
    icon: Car,
    subcategories: ['Steering Racks', 'Power Steering Pumps', 'Tie Rod Ends', 'Steering Columns', 'Steering Knuckles']
  },
  {
    id: 'electrical',
    name: 'Electrical',
    canonical: 'ELECTRICAL',
    icon: Cpu,
    subcategories: ['Alternators', 'Starter Motors', 'Batteries', 'Engine Control Units (ECU)', 'Wiring Harnesses', 'Sensors & Relays', 'Fuses']
  },
  {
    id: 'lighting',
    name: 'Lighting',
    canonical: 'LIGHTING',
    icon: Sun,
    subcategories: ['Headlights', 'Tail Lights', 'Fog Lights', 'Indicators & Blinkers', 'Brake Lights', 'Bulbs & LEDs']
  },
  {
    id: 'transmission',
    name: 'Transmission',
    canonical: 'TRANSMISSION',
    icon: Package,
    subcategories: ['Clutch Kits', 'Flywheels', 'CV Axles & Drive Shafts', 'Gearboxes', 'Differentials', 'Transmission Mounts', 'Torque Converters']
  },
  {
    id: 'cooling',
    name: 'Cooling',
    canonical: 'COOLING',
    icon: Clock,
    subcategories: ['Radiators', 'Water Pumps', 'Thermostats', 'Cooling Fans', 'Expansion Tanks', 'Coolant Hoses']
  },
  {
    id: 'hvac',
    name: 'Heating & AC',
    canonical: 'HVAC',
    icon: Clock,
    subcategories: ['AC Compressors', 'Condensers', 'Evaporator Cores', 'Blower Motors', 'Heater Cores', 'Expansion Valves']
  },
  {
    id: 'filtration',
    name: 'Filters & Service',
    canonical: 'FILTRATION',
    icon: CheckCircle2,
    subcategories: ['Oil Filters', 'Air Filters', 'Cabin Air Filters', 'Fuel Filters', 'Transmission Filters']
  },
  {
    id: 'ignition',
    name: 'Ignition',
    canonical: 'IGNITION',
    icon: Zap,
    subcategories: ['Spark Plugs', 'Ignition Coils & Packs', 'Glow Plugs', 'Distributors', 'Spark Plug Wires']
  },
  {
    id: 'fuel',
    name: 'Fuel System',
    canonical: 'FUEL SYSTEM',
    icon: Package,
    subcategories: ['Fuel Pumps', 'Fuel Injectors', 'Fuel Tanks', 'Fuel Pressure Regulators', 'Throttle Bodies']
  },
  {
    id: 'exhaust',
    name: 'Exhaust',
    canonical: 'EXHAUST',
    icon: Award,
    subcategories: ['Catalytic Converters', 'Mufflers', 'Exhaust Manifolds', 'Oxygen O2 Sensors', 'DPF Filters', 'Resonators']
  },
  {
    id: 'body',
    name: 'Body & Panels',
    canonical: 'BODY',
    icon: ShieldCheck,
    subcategories: ['Bumpers', 'Fenders', 'Bonnets & Hoods', 'Doors', 'Mirrors', 'Grilles', 'Windshields']
  },
  {
    id: 'wheels',
    name: 'Wheels & Tires',
    canonical: 'WHEELS',
    icon: Award,
    subcategories: ['Wheel Bearings', 'Hub Assemblies', 'Rims & Wheels', 'Tires', 'Lug Nuts & Studs']
  },
  {
    id: 'interior',
    name: 'Interior',
    canonical: 'INTERIOR',
    icon: Car,
    subcategories: ['Seats', 'Dashboards', 'Airbags', 'Steering Wheels', 'Center Consoles', 'Window Regulators']
  },
  {
    id: 'hardware',
    name: 'Hardware & Clips',
    canonical: 'HARDWARE',
    icon: Package,
    subcategories: ['Bolts & Screws', 'Nuts', 'Fasteners & Clips', 'Brackets', 'Grommets']
  },
  {
    id: 'ev',
    name: 'EV Components',
    canonical: 'EV',
    icon: Zap,
    subcategories: ['Traction Batteries', 'Electric Motors', 'Inverters', 'On-Board Chargers', 'Charge Ports', 'Battery Management (BMS)']
  },
  {
    id: 'adas',
    name: 'ADAS & Sensors',
    canonical: 'ADAS',
    icon: Radio,
    subcategories: ['Cameras', 'Radar Sensors', 'LiDAR Sensors', 'Ultrasonic Sensors', 'ADAS Control Modules', 'Parking Sensors']
  },
  {
    id: 'general',
    name: 'Fluids & Maintenance',
    canonical: 'GENERAL',
    icon: CheckCircle2,
    subcategories: ['Motor Oil', 'Brake Fluid', 'Transmission Fluid', 'Coolant Antifreeze', 'Power Steering Fluid', 'Cleaners & Degreasers']
  }
];

function matchProductCategory(p: any, categoryId: string): boolean {
  if (!categoryId || categoryId === 'all') return true;
  const pCat = String(p.category || '').toUpperCase().trim();
  const pTitle = `${p.title || ''} ${p.name || ''} ${p.description || ''} ${p.sku || ''}`.toLowerCase();

  switch (categoryId.toLowerCase()) {
    case 'braking':
    case 'brakes':
      return pCat === 'BRAKING' || pCat.includes('BRAK') || pTitle.includes('brake') || pTitle.includes('rotor') || pTitle.includes('pad') || pTitle.includes('caliper') || pTitle.includes('abs');
    case 'engine':
      return pCat === 'ENGINE' || (pCat.includes('ENG') && !pCat.includes('IGNIT')) || pTitle.includes('engine') || pTitle.includes('piston') || pTitle.includes('cylinder') || pTitle.includes('timing belt') || pTitle.includes('timing chain') || pTitle.includes('camshaft') || pTitle.includes('crankshaft') || pTitle.includes('gasket');
    case 'suspension':
      return pCat === 'SUSPENSION' || (pCat.includes('SUSP') && !pCat.includes('STEER')) || pTitle.includes('shock') || pTitle.includes('strut') || pTitle.includes('control arm') || pTitle.includes('ball joint') || pTitle.includes('sway bar') || pTitle.includes('coil spring');
    case 'steering':
      return pCat === 'STEERING' || pCat.includes('STEER') || pTitle.includes('steering') || pTitle.includes('tie rod') || pTitle.includes('rack') || pTitle.includes('power steering');
    case 'electrical':
      return pCat === 'ELECTRICAL' || (pCat.includes('ELECT') && !pCat.includes('LIGHT')) || pTitle.includes('alternator') || pTitle.includes('starter') || pTitle.includes('battery') || pTitle.includes('ecu') || pTitle.includes('wiring') || pTitle.includes('fuse');
    case 'lighting':
      return pCat === 'LIGHTING' || pCat.includes('LIGHT') || pTitle.includes('headlight') || pTitle.includes('tail light') || pTitle.includes('fog light') || pTitle.includes('lamp') || pTitle.includes('bulb') || pTitle.includes('indicator') || pTitle.includes('blinker');
    case 'transmission':
      return pCat === 'TRANSMISSION' || pCat.includes('TRANS') || pTitle.includes('transmission') || pTitle.includes('gearbox') || pTitle.includes('clutch') || pTitle.includes('flywheel') || pTitle.includes('cv axle') || pTitle.includes('differential') || pTitle.includes('driveshaft');
    case 'cooling':
      return pCat === 'COOLING' || (pCat.includes('COOL') && !pCat.includes('HVAC')) || pTitle.includes('radiator') || pTitle.includes('water pump') || pTitle.includes('thermostat') || pTitle.includes('cooling fan') || pTitle.includes('expansion tank') || pTitle.includes('coolant hose');
    case 'hvac':
      return pCat === 'HVAC' || pCat.includes('HVAC') || pCat.includes('AIR COND') || pTitle.includes('ac compressor') || pTitle.includes('condenser') || pTitle.includes('evaporator') || pTitle.includes('blower motor') || pTitle.includes('heater core');
    case 'filtration':
    case 'filters':
      return pCat === 'FILTRATION' || pCat.includes('FILTR') || pTitle.includes('filter') || pTitle.includes('oil filter') || pTitle.includes('air filter') || pTitle.includes('fuel filter') || pTitle.includes('cabin filter');
    case 'ignition':
      return pCat === 'IGNITION' || pCat.includes('IGNIT') || pTitle.includes('spark plug') || pTitle.includes('ignition coil') || pTitle.includes('distributor') || pTitle.includes('glow plug');
    case 'fuel':
      return pCat === 'FUEL SYSTEM' || pCat.includes('FUEL') || pTitle.includes('fuel pump') || pTitle.includes('fuel injector') || pTitle.includes('fuel tank') || pTitle.includes('throttle body');
    case 'exhaust':
      return pCat === 'EXHAUST' || pCat.includes('EXHAUST') || pTitle.includes('catalytic converter') || pTitle.includes('muffler') || pTitle.includes('exhaust') || pTitle.includes('downpipe') || pTitle.includes('dpf');
    case 'body':
      return pCat === 'BODY' || pCat.includes('BODY') || pTitle.includes('bumper') || pTitle.includes('fender') || pTitle.includes('hood') || pTitle.includes('door') || pTitle.includes('mirror') || pTitle.includes('grille');
    case 'wheels':
      return pCat === 'WHEELS' || pCat.includes('WHEEL') || pTitle.includes('wheel') || pTitle.includes('rim') || pTitle.includes('tire') || pTitle.includes('lug nut') || pTitle.includes('hub');
    case 'interior':
      return pCat === 'INTERIOR' || pCat.includes('INTERIOR') || pTitle.includes('seat') || pTitle.includes('dashboard') || pTitle.includes('airbag') || pTitle.includes('steering wheel');
    case 'hardware':
      return pCat === 'HARDWARE' || pCat.includes('HARDWARE') || pTitle.includes('bolt') || pTitle.includes('nut') || pTitle.includes('clip') || pTitle.includes('fastener');
    case 'ev':
      return pCat === 'EV' || pCat.includes('EV') || pTitle.includes('traction battery') || pTitle.includes('electric motor') || pTitle.includes('inverter') || pTitle.includes('on-board charger');
    case 'adas':
      return pCat === 'ADAS' || pCat.includes('ADAS') || pTitle.includes('radar') || pTitle.includes('lidar') || pTitle.includes('parking sensor') || pTitle.includes('camera module');
    case 'general':
      return pCat === 'GENERAL' || pCat.includes('GENERAL') || pTitle.includes('fluid') || pTitle.includes('oil') || pTitle.includes('lubricant') || pTitle.includes('tool');
    default:
      return pCat.toLowerCase() === categoryId.toLowerCase() || pTitle.includes(categoryId.toLowerCase());
  }
}

const CURATED_TOP_BRANDS = [
  { name: 'Bosch', origin: 'Germany', tag: 'Tier 1 OEM' },
  { name: 'Denso', origin: 'Japan', tag: 'OEM Toyota' },
  { name: 'Brembo', origin: 'Italy', tag: 'Performance' },
  { name: 'NGK / NTK', origin: 'Japan', tag: 'Ignition' },
  { name: 'Mann-Filter', origin: 'Germany', tag: 'Filtration' },
  { name: 'Ford Motorcraft', origin: 'USA', tag: 'OEM Ford' },
  { name: 'Bilstein', origin: 'Germany', tag: 'Suspension' },
  { name: 'Aisin', origin: 'Japan', tag: 'Drivetrain' },
  { name: 'KYB (Kayaba)', origin: 'Japan', tag: 'Hydraulics' },
  { name: 'Valeo', origin: 'France', tag: 'Thermal & Clutches' },
  { name: 'Delphi Technologies', origin: 'UK', tag: 'Fuel & Sensors' },
  { name: 'Toyota Genuine', origin: 'Japan', tag: 'Factory OEM' }
];

export default function ShopPageReplica({
  products,
  selectedCountry,
  isLoading = false,
  onProductClick,
  onVendorClick,
  onVinSearch,
  onRequestPart,
  onChatWithEve,
  activeVehicle,
  getWhatsAppHandoffUrl,
  searchQuery: initialSearchQuery,
  onSearchQueryChange,
  selectedCategory: propCategory,
  onCategoryChange
}: ShopPageReplicaProps) {
  // Filter States
  const [selectedMake, setSelectedMake] = useState(activeVehicle?.make || 'All Makes');
  const [selectedModel, setSelectedModel] = useState(activeVehicle?.model || 'All Models');
  const [selectedYear, setSelectedYear] = useState(activeVehicle?.year ? String(activeVehicle.year) : 'All Years');
  const [selectedTrim, setSelectedTrim] = useState(activeVehicle?.trim || 'All Trims');
  const [selectedEngine, setSelectedEngine] = useState(activeVehicle?.engine || 'All Engines');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(propCategory || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('All Brands');
  const [priceMax, setPriceMax] = useState(5000);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState('ALL');

  // Hero VIN Input State
  const [vinInput, setVinInput] = useState('');
  const [vinError, setVinError] = useState('');

  // Tab & Sort State
  const [activeTabCategory, setActiveTabCategory] = useState(propCategory || 'all');
  const [sortBy, setSortBy] = useState('featured');
  const [displayLimit, setDisplayLimit] = useState(24);

  // Sync parent search query
  React.useEffect(() => {
    if (initialSearchQuery !== undefined && initialSearchQuery !== searchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);

  // Sync parent category
  React.useEffect(() => {
    if (propCategory !== undefined && propCategory !== selectedCategory) {
      setSelectedCategory(propCategory);
      setActiveTabCategory(propCategory);
      setSelectedSubcategory('all');
    }
  }, [propCategory]);

  const handleCategorySelect = (catId: string) => {
    setSelectedCategory(catId);
    setSelectedSubcategory('all');
    setActiveTabCategory(catId);
    onCategoryChange?.(catId);
  };

  const handleQueryChange = (val: string) => {
    setSearchQuery(val);
    onSearchQueryChange?.(val);
  };

  const scrollToCatalog = () => {
    handleCategorySelect('all');
    if (typeof document !== 'undefined') {
      const el = document.getElementById('catalog-matrix');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Instant Suggestions for Search Dropdown
  const quickMatchingProducts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim().toLowerCase();
    return products.filter((p) => {
      const t = `${p.title || ''} ${p.name || ''} ${p.sku || ''} ${p.oem_number || p.oemNumber || ''} ${p.brand || ''}`.toLowerCase();
      return t.includes(q);
    }).slice(0, 4);
  }, [products, searchQuery]);

  const quickMatchingCategories = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim().toLowerCase();
    const set = new Set<string>();
    PART_KEYWORDS.forEach(pk => {
      if (pk.part.toLowerCase().includes(q) || pk.subcategory.toLowerCase().includes(q) || pk.category.toLowerCase().includes(q) || pk.keywords.toLowerCase().includes(q)) {
        set.add(pk.part);
      }
    });
    return Array.from(set).slice(0, 4);
  }, [searchQuery]);

  const quickMatchingMakes = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim().toLowerCase();
    return GLOBAL_MAKES.filter(m => m.name.toLowerCase().includes(q)).slice(0, 3);
  }, [searchQuery]);

  // Derived Exact Inventory Facets (Zero Exaggeration: Strictly reflect actual vendor products)
  const availableMakes = useMemo(() => {
    const makeSet = new Set<string>();
    products.forEach((p) => {
      const m = (p.make || p.brand || '').trim();
      if (m) makeSet.add(m);
    });
    if (makeSet.size > 0) {
      return ['All Makes', ...Array.from(makeSet).sort()];
    }
    return ['All Makes'];
  }, [products]);

  const popularMakesList = useMemo(() => {
    return availableMakes.filter((m) => m !== 'All Makes');
  }, [availableMakes]);

  const availableModels = useMemo(() => {
    if (selectedMake && selectedMake !== 'All Makes') {
      const modelsInProducts = new Set<string>();
      products.forEach((p) => {
        const pMake = (p.make || p.brand || '').trim().toLowerCase();
        if (pMake === selectedMake.toLowerCase() && p.model) {
          modelsInProducts.add(p.model.trim());
        }
      });
      if (modelsInProducts.size > 0) {
        return ['All Models', ...Array.from(modelsInProducts).sort()];
      }
      return ['All Models'];
    }
    return ['All Models'];
  }, [selectedMake, products]);

  const availableYears = useMemo(() => {
    const yearsInProducts = new Set<string>();
    products.forEach((p) => {
      if (selectedMake !== 'All Makes') {
        const pMake = (p.make || p.brand || '').trim().toLowerCase();
        if (pMake !== selectedMake.toLowerCase()) return;
      }
      if (selectedModel !== 'All Models') {
        const pModel = (p.model || '').trim().toLowerCase();
        if (pModel !== selectedModel.toLowerCase()) return;
      }
      if (p.year) {
        yearsInProducts.add(String(p.year).trim());
      } else if (p.compatibility && Array.isArray(p.compatibility)) {
        p.compatibility.forEach((c: any) => {
          if (c.year) yearsInProducts.add(String(c.year).trim());
        });
      }
    });
    if (yearsInProducts.size > 0) {
      return ['All Years', ...Array.from(yearsInProducts).sort((a, b) => Number(b) - Number(a))];
    }
    return ALL_YEARS;
  }, [products, selectedMake, selectedModel]);

  const availableBrands = useMemo(() => {
    const brandMap = new Map<string, number>();
    products.forEach((p) => {
      const b = (p.brand || p.make || '').trim();
      if (b) {
        brandMap.set(b, (brandMap.get(b) || 0) + 1);
      }
    });
    if (brandMap.size > 0) {
      return Array.from(brandMap.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => {
          const curated = CURATED_TOP_BRANDS.find((cb) => cb.name.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(cb.name.toLowerCase()));
          return {
            name,
            count,
            origin: curated?.origin || 'Verified',
            tag: curated?.tag || `${count} Part${count > 1 ? 's' : ''}`
          };
        });
    }
    return CURATED_TOP_BRANDS.map((b) => ({ ...b, count: 0 }));
  }, [products]);

  const availableTrims = useMemo(() => {
    if (selectedMake && selectedMake !== 'All Makes') {
      const specific = (MAKE_TRIMS as Record<string, string[]>)[selectedMake] || 
                       (MAKE_TRIMS as Record<string, string[]>)[selectedMake.replace(/-/g, ' ')] || [];
      if (specific.length > 0) return ['All Trims', ...specific];
    }
    return ['All Trims', ...CAR_TRIMS];
  }, [selectedMake]);

  const currencyCode = selectedCountry === 'GH' ? 'GHS' : 'NGN';
  const currObj = getCurrencyByCode(currencyCode);
  const currencySymbol = currObj.symbol || (currencyCode === 'GHS' ? 'GH₵' : '₦');

  const handleVinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanVin = vinInput.trim().toUpperCase();
    if (cleanVin.length !== 17) {
      setVinError('Please enter a valid 17-character VIN');
      return;
    }
    setVinError('');
    onVinSearch?.(cleanVin);
  };

  const handleResetFilters = () => {
    setSelectedMake('All Makes');
    setSelectedModel('All Models');
    setSelectedYear('All Years');
    setSelectedTrim('All Trims');
    setSelectedEngine('All Engines');
    handleQueryChange('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSelectedBrand('All Brands');
    setPriceMax(5000);
    setInStockOnly(false);
    setSelectedCondition('ALL');
    setActiveTabCategory('all');
  };

  // Mobile Filter Drawer Toggle State
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Dynamic category counts derived from real-time database products
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: products.length };
    STANDALONE_CATEGORIES.forEach((cat) => {
      if (cat.id !== 'all') {
        counts[cat.id] = products.filter((p) => matchProductCategory(p, cat.id)).length;
      }
    });
    return counts;
  }, [products]);

  // Zero Exaggeration Categories: Only categories with stock > 0 (always preserving 'all')
  const availableCategories = useMemo(() => {
    if (products.length > 0) {
      const active = STANDALONE_CATEGORIES.filter((cat) => cat.id === 'all' || (categoryCounts[cat.id] || 0) > 0);
      if (active.length > 1) return active;
    }
    return STANDALONE_CATEGORIES;
  }, [categoryCounts, products.length]);

  const displayCategoryTabs = useMemo(() => {
    const allTabs = STANDALONE_CATEGORIES.map((cat) => ({
      id: cat.id,
      label: cat.id === 'all' ? 'All Parts' : cat.name
    }));
    if (products.length > 0) {
      const active = allTabs.filter((tab) => tab.id === 'all' || (categoryCounts[tab.id] || 0) > 0);
      if (active.length > 1) return active;
    }
    return allTabs;
  }, [categoryCounts, products.length]);

  const hasActiveFilters = selectedMake !== 'All Makes' ||
    selectedModel !== 'All Models' ||
    selectedYear !== 'All Years' ||
    selectedTrim !== 'All Trims' ||
    selectedEngine !== 'All Engines' ||
    searchQuery.trim() !== '' ||
    selectedCategory !== 'all' ||
    selectedSubcategory !== 'all' ||
    selectedBrand !== 'All Brands' ||
    inStockOnly ||
    selectedCondition !== 'ALL';

  // Filtered products list with MMYTE + Keyword Search + Dynamic Facets
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    // Find synonyms/matching category from PART_KEYWORDS if search query is present
    const matchedCatalogKeywords = q ? PART_KEYWORDS.filter(k => 
      k.keywords.toLowerCase().includes(q) || 
      k.part.toLowerCase().includes(q) || 
      k.subcategory.toLowerCase().includes(q)
    ) : [];

    const activeVehicleFilter = selectedMake !== 'All Makes' ? {
      make: selectedMake,
      model: selectedModel !== 'All Models' ? selectedModel : '',
      year: selectedYear !== 'All Years' ? parseInt(selectedYear, 10) : 0,
      trim: selectedTrim !== 'All Trims' ? selectedTrim : undefined,
      engine: selectedEngine !== 'All Engines' ? selectedEngine : undefined
    } : null;

    const list = products.filter((p) => {
      // 1. Keyword search (Multi-token Name, Title, Description, SKU, OEM, Brand, Category, or Catalog Synonyms)
      if (q) {
        const itemText = `${p.title || ''} ${p.name || ''} ${p.description || ''} ${p.sku || ''} ${p.oem_number || p.oemNumber || ''} ${p.brand || ''} ${p.category || ''} ${p.subcategory || ''}`.toLowerCase();
        
        // Exact match
        if (itemText.includes(q)) {
          // Pass
        } else {
          // Token-based matching
          const tokens = q.split(/\s+/).filter(Boolean);
          const allTokensMatch = tokens.every((token) => {
            if (itemText.includes(token)) return true;
            return matchedCatalogKeywords.some((mk) => 
              mk.category.toLowerCase().includes(token) || 
              mk.subcategory.toLowerCase().includes(token) ||
              mk.part.toLowerCase().includes(token) ||
              mk.keywords.toLowerCase().includes(token)
            );
          });
          if (!allTokensMatch) return false;
        }
      }

      // 2. Strict Vehicle Make Filter (Zero cross-make leakage: Toyota parts never show when Honda/Nissan/etc is selected)
      if (selectedMake && selectedMake !== 'All Makes') {
        const targetMake = selectedMake.toLowerCase().trim();
        const pMake = (p.make || '').toLowerCase().trim();
        if (pMake) {
          if (pMake !== targetMake && !pMake.startsWith(targetMake) && !targetMake.startsWith(pMake)) {
            return false;
          }
        } else {
          const itemTitle = `${p.title || ''} ${p.name || ''}`.toLowerCase();
          const makeRegex = new RegExp(`(^|[^a-z0-9])${targetMake}([^a-z0-9]|$)`, 'i');
          if (!makeRegex.test(itemTitle)) {
            return false;
          }
        }
      }

      // 3. MMYTE Vehicle Compatibility
      if (activeVehicleFilter) {
        const fitment = matchProductToVehicle(p, activeVehicleFilter);
        if (!fitment.isCompatible) return false;
      } else {
        if (selectedYear !== 'All Years') {
          const pYr = String(p.year || '');
          const targetYr = String(selectedYear);
          const fullText = `${p.title || ''} ${p.name || ''} ${p.description || ''}`.toLowerCase();
          if (pYr && pYr !== targetYr && Math.abs(parseInt(pYr, 10) - parseInt(targetYr, 10)) > 3 && !fullText.includes(targetYr)) {
            return false;
          }
        }
        if (selectedEngine !== 'All Engines') {
          const pEng = String(p.engine || '').toLowerCase();
          const targetEng = selectedEngine.toLowerCase();
          const disp = (targetEng.match(/([0-9]+\.[0-9]+l?)/i) || [])[1];
          const fullText = `${p.title || ''} ${p.name || ''} ${p.description || ''} ${JSON.stringify(p.specifications || {})}`.toLowerCase();
          if (pEng && !pEng.includes(targetEng) && !(disp && fullText.includes(disp))) {
            return false;
          }
        }
      }

      // 3. Category filter
      const activeCat = activeTabCategory !== 'all' ? activeTabCategory : selectedCategory;
      if (activeCat !== 'all') {
        const catMatches = matchProductCategory(p, activeCat);
        if (!catMatches) return false;
      }

      // 4. Subcategory filter
      if (selectedSubcategory && selectedSubcategory !== 'all') {
        const subNorm = selectedSubcategory.toLowerCase().trim();
        const pSub = String(p.subcategory || '').toLowerCase().trim();
        const pTitle = `${p.title || ''} ${p.name || ''} ${p.description || ''}`.toLowerCase();
        if (!pSub.includes(subNorm) && !pTitle.includes(subNorm)) {
          return false;
        }
      }

      // 5. Brand filter
      if (selectedBrand !== 'All Brands') {
        const brandMatches = (p.brand || '').toLowerCase().includes(selectedBrand.toLowerCase());
        if (!brandMatches) return false;
      }

      // 6. Condition filter
      if (selectedCondition === 'oem') {
        const isOem = p.condition?.toUpperCase() === 'NEW' || p.brand?.toLowerCase().includes('genuine') || p.brand?.toLowerCase().includes('oem');
        if (!isOem) return false;
      }

      // 7. In stock filter
      if (inStockOnly && (p.stock === 0 || p.in_stock === false)) {
        return false;
      }

      // 8. Price Max
      if (p.price && p.price > priceMax) {
        return false;
      }

      return true;
    });

    // Sorting
    return list.sort((a, b) => {
      if (sortBy === 'price_asc' || sortBy === 'price-low') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price_desc' || sortBy === 'price-high') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      return 0; // featured/default
    });
  }, [
    products, 
    selectedMake, 
    selectedModel, 
    selectedYear, 
    selectedTrim, 
    selectedEngine, 
    searchQuery, 
    selectedCategory, 
    selectedSubcategory,
    activeTabCategory, 
    selectedBrand, 
    selectedCondition, 
    inStockOnly, 
    priceMax, 
    sortBy
  ]);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Mobile Filter Toggle Bar (Only visible on screens < lg) */}
      <div className="lg:hidden flex items-center justify-between gap-3 bg-white dark:bg-[#0D1424] p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <button
          type="button"
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-xs font-bold transition-all shadow-xs touch-manipulation min-h-[44px]"
        >
          <SlidersHorizontal className="size-4" />
          <span>{showMobileFilters ? 'Hide Vehicle & Category Filters' : 'Filter by Vehicle & Category'}</span>
          {hasActiveFilters && (
            <span className="size-2 rounded-full bg-amber-300 ml-1 animate-pulse" />
          )}
        </button>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleResetFilters}
            className="py-3 px-3.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 touch-manipulation min-h-[44px] flex items-center gap-1.5"
            title="Reset Filters"
          >
            <RotateCcw className="size-3.5" />
            <span className="hidden sm:inline">Reset</span>
          </button>
        )}
      </div>

      {/* 3-COLUMN MASTER GRID MATCHING IMAGE 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        
        {/* ========================================================================= */}
        {/* COLUMN 1: LEFT FILTER SIDEBAR (3 COLS) */}
        {/* ========================================================================= */}
        <aside className={`lg:col-span-3 space-y-5 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}>
          {/* 1. Vehicle Selector Widget */}
          <div className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800/80 pb-3">
              <Car className="size-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Select Your Vehicle
              </h3>
            </div>

            {/* Quick Brand Badges */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400">Popular Makes</label>
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                {popularMakesList.map((mk) => (
                  <button
                    key={mk}
                    type="button"
                    onClick={() => {
                      const next = mk === selectedMake ? 'All Makes' : mk;
                      setSelectedMake(next);
                      setSelectedModel('All Models');
                      setSelectedTrim('All Trims');
                      setSelectedEngine('All Engines');
                    }}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-xl text-[10px] font-bold transition-all shrink-0 border ${
                      selectedMake === mk
                        ? 'bg-blue-50 border-blue-600 text-blue-700 dark:bg-blue-950/60 dark:border-blue-400 dark:text-blue-300 shadow-xs'
                        : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-blue-300'
                    }`}
                  >
                    <VehicleBrandLogo brand={mk} size="xs" variant="badge" />
                    <span>{mk}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Make */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Make</label>
                {selectedMake !== 'All Makes' && (
                  <VehicleBrandLogo brand={selectedMake} size="xs" variant="badge" />
                )}
              </div>
              <div className="relative">
                <select
                  value={selectedMake}
                  onChange={(e) => {
                    setSelectedMake(e.target.value);
                    setSelectedModel('All Models');
                    setSelectedTrim('All Trims');
                    setSelectedEngine('All Engines');
                  }}
                  className="w-full appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {availableMakes.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <ChevronDown className="size-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Model */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Model</label>
              <div className="relative">
                <select
                  value={selectedModel}
                  onChange={(e) => {
                    setSelectedModel(e.target.value);
                    setSelectedTrim('All Trims');
                  }}
                  disabled={selectedMake === 'All Makes'}
                  className="w-full appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                >
                  {availableModels.map((mod) => (
                    <option key={mod} value={mod}>{mod}</option>
                  ))}
                </select>
                <ChevronDown className="size-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Year */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Year</label>
              <div className="relative">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <ChevronDown className="size-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Trim */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Trim / Edition</label>
              <div className="relative">
                <select
                  value={selectedTrim}
                  onChange={(e) => setSelectedTrim(e.target.value)}
                  disabled={selectedMake === 'All Makes'}
                  className="w-full appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                >
                  {availableTrims.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <ChevronDown className="size-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Engine */}
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Engine / Powertrain</label>
              <div className="relative">
                <select
                  value={selectedEngine}
                  onChange={(e) => setSelectedEngine(e.target.value)}
                  className="w-full appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  {ALL_ENGINES.map((eng) => (
                    <option key={eng} value={eng}>{eng}</option>
                  ))}
                </select>
                <ChevronDown className="size-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* 2. Standalone Categories & Subcategories Filter List */}
          <div className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Categories
              </h3>
              {selectedCategory !== 'all' && (
                <button
                  type="button"
                  onClick={() => handleCategorySelect('all')}
                  className="text-[10px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="space-y-1 max-h-80 overflow-y-auto no-scrollbar">
              {availableCategories.map((cat) => {
                const isSelected = selectedCategory === cat.id;
                const IconComponent = cat.icon;
                const matchingSubcategories = cat.subcategories.filter((sub) => {
                  if (products.length === 0) return true;
                  return products.some((p) => {
                    if (!matchProductCategory(p, cat.id)) return false;
                    const text = `${p.title || ''} ${p.name || ''} ${p.subcategory || ''} ${p.description || ''}`.toLowerCase();
                    return text.includes(sub.toLowerCase());
                  });
                });
                const subcategoriesToRender = matchingSubcategories.length > 0 ? matchingSubcategories : cat.subcategories;

                return (
                  <div key={cat.id} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => handleCategorySelect(cat.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                        isSelected
                          ? 'bg-blue-600 text-white font-bold shadow-xs'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate">
                        {cat.id === 'all' ? (
                          <Layers className="size-3.5 shrink-0 opacity-80" />
                        ) : (
                          <CategoryIcon
                            category={cat.canonical || cat.id}
                            size="xs"
                            variant="raw"
                            className="size-3.5 shrink-0 object-contain"
                          />
                        )}
                        <span className="truncate">{cat.name}</span>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono ${
                        isSelected ? 'bg-white/20 text-white font-bold' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                      }`}>
                        {categoryCounts[cat.id] ?? (cat.id === 'all' ? products.length : 0)}
                      </span>
                    </button>

                    {/* Child Subcategories (Revealed when Category is Selected) */}
                    {isSelected && subcategoriesToRender.length > 0 && (
                      <div className="pl-5 pr-1 py-1 space-y-0.5 border-l-2 border-blue-500/40 ml-3.5 my-1">
                        <button
                          type="button"
                          onClick={() => setSelectedSubcategory('all')}
                          className={`w-full text-left px-2 py-1 rounded-lg text-[11px] transition-colors ${
                            selectedSubcategory === 'all'
                              ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 font-bold'
                              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                          }`}
                        >
                          All {cat.name}
                        </button>
                        {subcategoriesToRender.map((sub) => {
                          const isSubActive = selectedSubcategory.toLowerCase() === sub.toLowerCase();
                          return (
                            <button
                              key={sub}
                              type="button"
                              onClick={() => setSelectedSubcategory(sub)}
                              className={`w-full text-left px-2 py-1 rounded-lg text-[11px] transition-colors truncate ${
                                isSubActive
                                  ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 font-bold'
                                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                              }`}
                            >
                              {sub}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. Price Filter Slider */}
          <div className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Price Range
              </h3>
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                Up to {currencySymbol}{priceMax.toLocaleString()}
              </span>
            </div>

            <input
              type="range"
              min={100}
              max={5000}
              step={50}
              value={priceMax}
              onChange={(e) => setPriceMax(Number(e.target.value))}
              className="w-full accent-blue-600 cursor-pointer"
            />

            <div className="flex items-center justify-between text-[11px] text-slate-400 font-semibold">
              <span>{currencySymbol}100</span>
              <span>{currencySymbol}5,000+</span>
            </div>
          </div>

          {/* 4. Part Condition & In Stock Checkboxes */}
          <div className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm space-y-3">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Condition & Stock
            </h3>

            <div className="space-y-2">
              <label
                onClick={() => setInStockOnly(!inStockOnly)}
                className="flex items-center gap-2.5 text-xs text-slate-700 dark:text-slate-300 cursor-pointer font-medium hover:text-blue-600"
              >
                <div className={`size-4 rounded border flex items-center justify-center transition-colors ${
                  inStockOnly ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 dark:border-slate-700'
                }`}>
                  {inStockOnly && <Check className="size-3" />}
                </div>
                <span>In Stock Only</span>
              </label>

              <label
                onClick={() => setSelectedCondition(selectedCondition === 'oem' ? 'ALL' : 'oem')}
                className="flex items-center gap-2.5 text-xs text-slate-700 dark:text-slate-300 cursor-pointer font-medium hover:text-blue-600"
              >
                <div className={`size-4 rounded border flex items-center justify-center transition-colors ${
                  selectedCondition === 'oem' ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 dark:border-slate-700'
                }`}>
                  {selectedCondition === 'oem' && <Check className="size-3" />}
                </div>
                <span>100% Genuine OEM</span>
              </label>
            </div>
          </div>

          {/* Reset Filters */}
          <button
            type="button"
            onClick={handleResetFilters}
            className="w-full py-2.5 px-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors flex items-center justify-center gap-1.5"
          >
            <RotateCcw className="size-3.5" />
            <span>Reset All Filters</span>
          </button>
        </aside>

        {/* ========================================================================= */}
        {/* COLUMN 2: MAIN CONTENT (6 COLS) */}
        {/* ========================================================================= */}
        <main className="lg:col-span-6 space-y-6">
          {/* 1. CINEMATIC HERO WITH VIN SEARCH CAPSULE (Matches Image 1) */}
          <section className="relative rounded-3xl overflow-hidden shadow-sm bg-[#0A1B4A] text-white p-6 sm:p-8 border border-slate-800">
            {/* Background Car Silhouette */}
            <div
              className="absolute right-0 bottom-0 w-1/2 h-full bg-cover bg-no-repeat bg-right opacity-25 pointer-events-none mix-blend-luminosity"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1617814076367-b759c7d7e738?auto=format&fit=crop&w=800&q=80')`
              }}
            />

            <div className="relative z-10 space-y-4 max-w-xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white border border-white/20">
                <ShieldCheck className="size-3.5 text-emerald-400" />
                <span>Instant Fitment Guarantee • Free VIN Lookup</span>
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight">
                Find Exactly What Fits Your Vehicle
              </h1>

              <p className="text-xs sm:text-sm text-blue-100 font-medium leading-relaxed">
                Search over 150,000+ genuine OEM & aftermarket parts verified directly from West Africa's leading commercial depots.
              </p>

              {/* Dual Search: Keyword + VIN Search */}
              <div className="space-y-2 pt-2">
                {/* Part Name / Keyword Search Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setIsSearchFocused(false);
                    scrollToCatalog();
                  }}
                  className="relative"
                >
                  <div className="relative flex items-center bg-white/10 p-1.5 pl-3.5 rounded-2xl border border-white/20 shadow-sm focus-within:ring-2 focus-within:ring-blue-400">
                    <Search className="size-4 text-blue-200 shrink-0 mr-2.5" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleQueryChange(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => {
                        setTimeout(() => setIsSearchFocused(false), 250);
                      }}
                      placeholder="Search parts by name, OEM #, keywords (e.g. Brake Pads, Denso Starter, Alternator...)"
                      className="w-full bg-transparent py-2 text-xs font-semibold text-white placeholder:text-blue-200/60 focus:outline-none"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          handleQueryChange('');
                          setIsSearchFocused(false);
                        }}
                        className="text-blue-200 hover:text-white p-1 mr-1 transition-colors"
                        title="Clear search"
                        aria-label="Clear search"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                    <button
                      type="submit"
                      className="px-4 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-extrabold text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm active:scale-95 shrink-0"
                    >
                      <Search className="size-3.5" />
                      <span>Search</span>
                    </button>
                  </div>

                  {/* Instant Suggestions & Autocomplete Overlay */}
                  {isSearchFocused && searchQuery.trim().length > 0 && (
                    <div
                      className="absolute top-full left-0 right-0 mt-2 z-50 bg-[#070E22] border border-white/20 rounded-2xl shadow-xl p-3.5 text-white max-h-96 overflow-y-auto space-y-3"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-white/10 text-[11px] font-bold text-blue-300">
                        <span>Instant Suggestions</span>
                        <span className="text-white/60">{filteredProducts.length} matching parts found</span>
                      </div>

                      {/* Matching Part Types */}
                      {quickMatchingCategories.length > 0 && (
                        <div>
                          <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                            Matching Part Types
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {quickMatchingCategories.map((partName) => (
                              <button
                                key={partName}
                                type="button"
                                onClick={() => {
                                  handleQueryChange(partName);
                                  setIsSearchFocused(false);
                                  scrollToCatalog();
                                }}
                                className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-blue-600/60 text-xs font-semibold text-slate-100 transition-colors flex items-center gap-1"
                              >
                                <Search className="size-3 text-blue-400" />
                                <span>{partName}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Matching Makes */}
                      {quickMatchingMakes.length > 0 && (
                        <div>
                          <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                            Vehicle Makes
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {quickMatchingMakes.map((m) => (
                              <button
                                key={m.name}
                                type="button"
                                onClick={() => {
                                  setSelectedMake(m.name);
                                  setIsSearchFocused(false);
                                  scrollToCatalog();
                                }}
                                className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-blue-600/60 text-xs font-semibold text-slate-100 transition-colors flex items-center gap-1.5"
                              >
                                <VehicleBrandLogo brand={m.name} size="xs" variant="raw" className="size-4 object-contain" />
                                <span>{m.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Matching Direct Products */}
                      {quickMatchingProducts.length > 0 && (
                        <div className="space-y-1.5">
                          <span className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1">
                            Direct Catalog Parts
                          </span>
                          {quickMatchingProducts.map((p) => (
                            <div
                              key={p.id}
                              onClick={() => {
                                setIsSearchFocused(false);
                                onProductClick(p);
                              }}
                              className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/10 cursor-pointer transition-colors group"
                            >
                              <div className="size-10 rounded-lg overflow-hidden bg-white/5 shrink-0 border border-white/10">
                                <img
                                  src={
                                    p.image_urls
                                      ? typeof p.image_urls === 'string'
                                        ? JSON.parse(p.image_urls || '[]')[0] || p.image_url
                                        : p.image_urls[0]
                                      : p.image_url || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80&w=200'
                                  }
                                  alt={`${p.make || ''} ${p.model || ''} ${p.year || ''} ${p.title || p.name || 'Auto Part'} - Verified Fitment`.trim()}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-white truncate group-hover:text-blue-400">
                                  {p.title || p.name}
                                </p>
                                <p className="text-[10px] text-white/60 truncate">
                                  {p.brand || 'OEM'} {p.oem_number ? `• OEM #${p.oem_number}` : ''}
                                </p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-xs font-extrabold text-blue-400">
                                  {currencySymbol}{Number(p.price || 0).toLocaleString()}
                                </p>
                                <span className="text-[9px] text-emerald-400 font-semibold">In Stock</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* View All Action */}
                      <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                        <span className="text-[11px] text-white/60">Press Enter or click</span>
                        <button
                          type="button"
                          onClick={() => {
                            setIsSearchFocused(false);
                            scrollToCatalog();
                          }}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-bold text-white transition-colors flex items-center gap-1.5"
                        >
                          <span>View {filteredProducts.length} Results</span>
                          <ArrowRight className="size-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </form>

                {/* VIN Search Capsule */}
                <form onSubmit={handleVinSubmit}>
                  <div className="relative flex flex-col sm:flex-row items-stretch gap-2 bg-white/10 p-1.5 rounded-2xl border border-white/20 shadow-sm">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        maxLength={17}
                        value={vinInput}
                        onChange={(e) => {
                          setVinInput(e.target.value.toUpperCase());
                          if (vinError) setVinError('');
                        }}
                        placeholder="Or enter 17-digit VIN (e.g. 1HGCR2F83HA...)"
                        className="w-full bg-transparent pl-4 pr-3 py-2.5 text-xs font-mono font-bold text-white placeholder:text-blue-200/60 focus:outline-none"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-5 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-extrabold text-xs transition-colors flex items-center justify-center gap-2 shadow-sm active:scale-95 shrink-0"
                    >
                      <FileCheck className="size-3.5" />
                      <span>Decode & Match</span>
                    </button>
                  </div>
                  {vinError && (
                    <p className="text-[11px] text-amber-300 font-semibold mt-1.5 ml-2">
                      {vinError}
                    </p>
                  )}
                </form>
              </div>

              {/* Popular Searches Pills */}
              <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-blue-200">
                <span className="font-semibold text-white/80">Popular:</span>
                {['Brake Pads', 'Shock Absorber', 'Oil Filter', 'Spark Plugs', 'Alternator', 'Control Arm'].map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => {
                      handleQueryChange(term);
                      setSelectedCategory('all');
                      setActiveTabCategory('all');
                      scrollToCatalog();
                    }}
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${
                      searchQuery.toLowerCase() === term.toLowerCase()
                        ? 'bg-blue-500 text-white shadow-sm'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* "SHOP BY VEHICLE MAKE" (Official Brand Logos Grid) */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Car className="size-4 text-blue-600 dark:text-blue-400" />
                <h2 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                  Shop by Vehicle Make
                </h2>
              </div>
              {selectedMake !== 'All Makes' ? (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedMake('All Makes');
                    setSelectedModel('All Models');
                  }}
                  className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <span>Reset Make ({selectedMake})</span>
                  <RotateCcw className="size-3" />
                </button>
              ) : (
                <span className="text-xs font-semibold text-slate-400">
                  Select your vehicle brand
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-1">
              {popularMakesList.map((makeName) => {
                const isSelected = selectedMake.toLowerCase() === makeName.toLowerCase();
                return (
                  <button
                    key={makeName}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedMake('All Makes');
                        setSelectedModel('All Models');
                      } else {
                        setSelectedMake(makeName);
                        setSelectedModel('All Models');
                      }
                    }}
                    className="group flex flex-col items-center justify-center text-center cursor-pointer p-1 transition-transform hover:-translate-y-0.5 focus:outline-hidden"
                  >
                    <VehicleBrandLogo
                      brand={makeName}
                      size="md"
                      variant="badge"
                      className={`transition-all duration-200 ${
                        isSelected
                          ? 'border-2 border-blue-600 ring-2 ring-blue-500/20 shadow-xs scale-105'
                          : 'group-hover:border-blue-400 group-hover:scale-105 group-hover:shadow-xs'
                      }`}
                    />
                    <span className={`mt-1.5 text-[11px] sm:text-xs font-bold truncate max-w-[68px] sm:max-w-[76px] transition-colors leading-tight ${
                      isSelected
                        ? 'text-blue-600 dark:text-blue-400 font-extrabold'
                        : 'text-slate-700 dark:text-slate-300 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                    }`}>
                      {makeName}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* 4. "GENUINE PARTS. TRUSTED SELLERS." WIDE BANNER (Matches Image 1) */}
          <section className="rounded-2xl bg-slate-900 text-white p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 border border-slate-800 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-blue-600/20 text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/30">
                <ShieldCheck className="size-6" />
              </div>
              <div>
                <h3 className="text-sm font-black tracking-tight">Genuine Parts. Trusted Sellers.</h3>
                <p className="text-[11px] text-slate-400 font-medium">
                  Every merchant is depot-verified with vehicle fitment warranty and escrow release.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs font-semibold shrink-0">
              <span className="flex items-center gap-1 text-emerald-400">
                <CheckCircle2 className="size-4" /> 100% Fitment
              </span>
              <span className="flex items-center gap-1 text-blue-400">
                <Truck className="size-4" /> 24h Dispatch
              </span>
            </div>
          </section>

          {/* 4. POPULAR PRODUCTS TABBED CATALOG MATRIX (Matches Image 1) */}
          <section id="catalog-matrix" className="space-y-4 scroll-mt-20">
            {/* Active Search / Filter Banner */}
            {searchQuery.trim() && (
              <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/60 rounded-2xl p-3.5 text-xs text-slate-800 dark:text-blue-100 shadow-xs">
                <div className="flex items-center gap-2.5">
                  <div className="size-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <Search className="size-4" />
                  </div>
                  <div>
                    <span className="font-semibold text-slate-600 dark:text-slate-300">Searching verified parts for: </span>
                    <strong className="font-black text-blue-600 dark:text-blue-400">"{searchQuery.trim()}"</strong>
                    <span className="ml-2 font-bold text-slate-500 dark:text-slate-400">
                      ({filteredProducts.length} {filteredProducts.length === 1 ? 'part' : 'parts'} found)
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleQueryChange('')}
                  className="px-3 py-1.5 rounded-xl bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 hover:text-red-500 font-bold text-xs border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 transition-colors shadow-xs"
                >
                  <X className="size-3.5" />
                  <span>Clear Search</span>
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-3.5 shadow-sm">
              {/* Category Tabs */}
              <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                {displayCategoryTabs.map((tab) => {
                  const isActive = (activeTabCategory === tab.id) || (selectedCategory === tab.id);
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleCategorySelect(tab.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      {tab.id !== 'all' && (
                        <CategoryIcon
                          category={tab.id}
                          size="xs"
                          variant="raw"
                          className="size-3.5 shrink-0 object-contain"
                        />
                      )}
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Sort Selector */}
              <div className="relative shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-bold rounded-xl px-3 py-1.5 pr-8 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                >
                  <option value="featured">Sort: Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                </select>
                <ChevronDown className="size-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Active Subcategory Pill Indicator */}
            {selectedSubcategory !== 'all' && (
              <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/60 text-xs">
                <span className="text-slate-500 dark:text-slate-400 font-medium">Subcategory:</span>
                <span className="font-bold text-blue-700 dark:text-blue-300">{selectedSubcategory}</span>
                <button
                  type="button"
                  onClick={() => setSelectedSubcategory('all')}
                  className="ml-auto text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-0.5 rounded-md"
                  title="Remove subcategory filter"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            )}

            {/* Product Cards Grid */}
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-3.5 space-y-3 animate-pulse h-80 flex flex-col justify-between">
                    <div className="space-y-2.5">
                      <div className="h-4 w-24 bg-slate-200 dark:bg-slate-800 rounded-full" />
                      <div className="w-full aspect-square bg-slate-100 dark:bg-slate-800/60 rounded-xl" />
                      <div className="space-y-1 pt-1">
                        <div className="h-3 w-16 bg-slate-200 dark:bg-slate-800 rounded" />
                        <div className="h-4 w-full bg-slate-200 dark:bg-slate-800 rounded" />
                        <div className="h-3 w-24 bg-slate-200 dark:bg-slate-800 rounded" />
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                      <div className="h-5 w-20 bg-slate-200 dark:bg-slate-800 rounded" />
                      <div className="h-8 w-24 bg-slate-200 dark:bg-slate-800 rounded-xl" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12 px-4 bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                <Package className="size-12 text-slate-400 mx-auto" />
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-slate-800 dark:text-slate-200">
                    No components found matching your criteria
                  </h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    We source parts directly from verified automotive merchants across Abossey Okai (Accra) and Ladipo (Lagos). Request your part via WhatsApp or join as a verified merchant.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors"
                  >
                    Reset Filters
                  </button>
                  <a
                    href={`https://wa.me/233539572421?text=${encodeURIComponent(
                      `Hi Parts4Drive! I am looking for a part${searchQuery ? ' matching "' + searchQuery + '"' : ''}${selectedMake !== 'All Makes' ? ' for ' + selectedMake : ''}. Can you help me find it from verified merchants?`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm transition-all"
                  >
                    <MessageCircle className="size-3.5" />
                    <span>Request on WhatsApp</span>
                  </a>
                  <a
                    href="/vendor"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-sm transition-all"
                  >
                    <Store className="size-3.5" />
                    <span>Become a Verified Seller</span>
                  </a>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {filteredProducts.slice(0, displayLimit).map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      country={selectedCountry}
                      onSelect={() => onProductClick(product)}
                      activeVehicle={activeVehicle}
                      whatsappUrl={getWhatsAppHandoffUrl ? getWhatsAppHandoffUrl(product, activeVehicle) : undefined}
                      onVendorClick={onVendorClick}
                    />
                  ))}
                </div>

                {filteredProducts.length > displayLimit && (
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={() => setDisplayLimit((prev) => prev + 24)}
                      className="px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 text-xs font-bold transition-all shadow-md active:scale-95"
                    >
                      Load More Verified Parts ({filteredProducts.length - displayLimit} remaining)
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        </main>

        {/* ========================================================================= */}
        {/* COLUMN 3: RIGHT COLUMN TRUST & TOP BRANDS (3 COLS) */}
        {/* ========================================================================= */}
        <aside className="lg:col-span-3 space-y-5">
          {/* 1. Why Shop With Parts4Drive Card */}
          <div className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-4">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
              <Award className="size-4 text-blue-600" />
              <span>Why Shop With Us?</span>
            </h3>

            <div className="space-y-3 text-xs">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="size-4 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white">Genuine OEM Guarantee</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">All parts sourced directly from verified authorized depots.</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <CreditCard className="size-4 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white">Escrow Protection</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Payments released only when you confirm fitment.</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Truck className="size-4 text-indigo-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white">24-48h Delivery</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Direct courier dispatch across Ghana and Nigeria.</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <ShieldCheck className="size-4 text-purple-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 dark:text-white">14-Day Returns</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Hassle-free return policy if fitment does not match.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Top Brands Matrix */}
          <div className="bg-white dark:bg-[#0D1424] rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200">
                Top Brands
              </h3>
              {selectedBrand !== 'All Brands' && (
                <button
                  type="button"
                  onClick={() => setSelectedBrand('All Brands')}
                  className="text-[10px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {availableBrands.map((b) => (
                <button
                  key={b.name}
                  type="button"
                  onClick={() => setSelectedBrand(selectedBrand === b.name ? 'All Brands' : b.name)}
                  className={`p-2.5 rounded-xl border transition-all text-left group flex items-center gap-2.5 ${
                    selectedBrand === b.name
                      ? 'bg-blue-50/90 dark:bg-blue-950/50 border-blue-500 shadow-xs'
                      : 'bg-slate-50 dark:bg-slate-900/60 border-slate-200/60 dark:border-slate-800 hover:border-blue-500'
                  }`}
                >
                  <VehicleBrandLogo
                    brand={b.name}
                    size="sm"
                    variant="badge"
                    className="shrink-0 group-hover:scale-105 transition-transform"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold text-xs text-slate-800 dark:text-slate-200 group-hover:text-blue-600 truncate">
                      {b.name}
                    </div>
                    <div className="text-[10px] text-slate-400 font-semibold truncate">
                      {b.tag} • {b.origin}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 3. Need Help Finding a Part? Card */}
          <div className="bg-[#0A1B4A] text-white rounded-2xl p-5 shadow-sm border border-slate-800 space-y-3">
            <div className="size-9 rounded-xl bg-white/10 flex items-center justify-center">
              <MessageCircle className="size-5 text-emerald-400" />
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-black">Need Help Finding a Part?</h3>
              <p className="text-[11px] text-blue-200 leading-relaxed">
                Send your vehicle VIN or photo to our parts specialists. We'll locate the exact component within minutes.
              </p>
            </div>

            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={onRequestPart}
                className="w-full py-2.5 px-3 rounded-xl bg-white hover:bg-blue-50 text-blue-900 font-bold text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm"
              >
                <span>Request Custom Part</span>
                <ArrowRight className="size-3.5" />
              </button>

              <button
                type="button"
                onClick={onChatWithEve}
                className="w-full py-2.5 px-3 rounded-xl bg-white/15 hover:bg-white/20 text-white font-bold text-xs transition-colors flex items-center justify-center gap-1.5 border border-white/20"
              >
                <MessageCircle className="size-3.5 text-blue-300" />
                <span>Consult Parts Specialist</span>
              </button>
            </div>
          </div>
        </aside>

      </div>
    </div>
  );
}
